import { Controller } from "stimulus";
import { formatMoney } from "utils/money";
import { applyAvatar } from "utils/avatar";
import { countdown } from "utils/live_poll";
import { postJson, deleteJson } from "utils/json_request";
import { LiveRoomClient } from "live_chat/room_client";
import { SHADOW_ID_PREFIX } from "live_chat/history_buffer";
import { normalizeMessage } from "live_chat/normalize";
import { jitter } from "live_chat/backoff";
import * as messageRenderer from "live_chat/message_renderer";

// The counter only appears once a message is this close (or closer) to the input's maxlength,
// so it stays out of the way for the vast majority of short messages.
const COUNTER_VISIBLE_REMAINING = 50;

// A fast double-click on the trigger icon lands its second click on the backdrop the dialog just
// opened over (same screen position), so a backdrop dismiss within this window of opening is ignored.
const CONFIRM_DISMISS_GRACE_MS = 250;

// A send that neither echoes nor settles within this is surfaced as failed with a retry - the SDK
// never times out a pending send promise itself, so a half-open socket would otherwise hang it forever.
const SEND_TIMEOUT_MS = 8000;

// A send timeout cycles the socket (new connection, new token) at most once per this window.
const REFRESH_MIN_INTERVAL_MS = 30000;

// A go-live or stream-end refresh is spread over this window so an audience does not reload together.
const STREAM_EVENT_REFRESH_SPREAD_MS = 5000;

// A shadow-banned viewer's fake echo lands after a plausible round-trip, never instantly.
const SHADOW_ECHO_MIN_MS = 150;
const SHADOW_ECHO_SPREAD_MS = 250;

// Mirrors ChatContext::TIMEOUT_DURATIONS (app/services/chat_context.rb) exactly - the server-side
// allowlist for the timeouts endpoint's `seconds` param. Any other value is refused server-side, so
// these three duration chips ARE the client-side validation; there is no free-text duration input.
const TIMEOUT_DURATIONS = [60, 600, 3600];
const TIMEOUT_DEFAULT_SECONDS = 600;

// The confirm dialog's Tab trap re-queries this on every Tab press rather than caching a stop
// list, so a variant switch (or the timeout variant's own chips/textarea) is picked up live.
const CONFIRM_FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

// Live chat over an IVS Chat room. The transport (socket, token, reconnects, heartbeat) is the
// shared room client's (live_chat/room_client.js); this controller renders its frames, sends
// messages through it, and talks to the server over the subscriber_v2/live_chat JSON endpoints
// for everything the server must stay authoritative on (votes, moderation).
export default class ChatPanel extends Controller {
  static targets = [
    "messages", "input", "submit", "closed", "timedOut", "timedOutCountdown", "disconnected", "hint",
    "sendFailedNotice", "counter", "deleteIcon", "banIcon", "timeoutIcon", "ellipsisIcon",
    "moderatorBadge", "announcement", "announcementBody", "announcementText", "announcementToggle",
    "announcementPlaceholder", "messageTemplate", "pendingStatusTemplate", "tipMessageTemplate",
    "privateMessageTemplate", "moderatorActionsTemplate", "tipModeratorActionsTemplate", "confirm",
    "confirmCancel", "confirmAccept", "confirmPreview", "confirmPreviewAvatar", "confirmPreviewAvatarFallback",
    "confirmPreviewName", "confirmPreviewAmount", "confirmPreviewBody", "confirmTitleName",
    "timeoutDurationChip", "timeoutReason", "reconnecting", "slowMode", "slowModeCountdown",
  ];
  static values = {
    currentUserId: Number,
    moderator: Boolean,
    contextType: String,
    contextId: Number,
    heartbeatIntervalMs: Number,
    subscriberProfileBaseUrl: String,
    // The subscriber_v2/live_chat endpoint paths: session, heartbeat, messages, bans, timeouts,
    // tips, and (viewer page only) votes.
    endpoints: Object,
    // The viewer page re-renders itself on stream_started / stream_ended (player, title, copy);
    // the console refreshes through its own Turbo stream instead.
    refreshOnStreamEvents: Boolean,
    // Set only by the live console, where the banner is editable in place. An editable banner has
    // to stay on screen when empty, or there is nothing left for the creator to click.
    announcementEditable: Boolean,
  };

  connect() {
    this.closed = false;
    this.timedOut = false;
    this.timeoutTimer = null;
    // The viewer block of the latest bootstrap (flags and identity); null until the first one lands.
    this.viewer = null;
    this.lastRefreshAt = 0;
    this.everConnected = false;
    this.slowModeSeconds = 0;
    this.slowModeUntil = 0;
    this.slowModeTimer = null;
    this.slowModeLocked = false;
    this.selectedTimeoutSeconds = TIMEOUT_DEFAULT_SECONDS;
    this.announcementExpanded = false;
    // client_msg_id -> { body, row, timer, resent, failed }. An entry exists from send() until its
    // echo arrives (see received()'s "message" branch) or it's abandoned as failed.
    this.pendingMessages = new Map();
    // Tracks whether the reader was at the bottom of the list, kept current via the scroll
    // listener below. Initialized eagerly (not left undefined) so the very first keyboard-open
    // resize on mobile already has a valid value to re-pin against.
    this.pinnedToBottom = true;

    // Stimulus singular target getters are not memoized (each access re-queries the
    // controller's subtree, which only grows as messages accumulate for the session), so
    // resolve these once and reuse the cached elements from the per-message build methods.
    this.messageTemplateEl = this.messageTemplateTarget;
    this.pendingStatusTemplateEl = this.pendingStatusTemplateTarget;
    this.tipMessageTemplateEl = this.tipMessageTemplateTarget;
    this.privateMessageTemplateEl = this.privateMessageTemplateTarget;
    if (this.hasModeratorActionsTemplateTarget) this.moderatorActionsTemplateEl = this.moderatorActionsTemplateTarget;
    if (this.hasTipModeratorActionsTemplateTarget) this.tipModeratorActionsTemplateEl = this.tipModeratorActionsTemplateTarget;

    this.pendingConfirm = null;
    this.confirmTrigger = null;
    this.confirmOpenedAt = 0;
    if (this.hasConfirmTarget) this.confirmTarget.classList.add("hidden");

    this.onConfirmKeydown = (event) => {
      if (!this.pendingConfirm) return;

      if (event.key === "Escape") {
        event.preventDefault();
        this.cancelConfirm();
        return;
      }
      if (event.key !== "Tab") return;

      event.preventDefault();
      const stops = this.confirmFocusableElements();
      if (stops.length === 0) return;

      const currentIndex = stops.indexOf(document.activeElement);
      const nextIndex = (currentIndex + (event.shiftKey ? -1 : 1) + stops.length) % stops.length;
      stops[nextIndex].focus();
    };
    document.addEventListener("keydown", this.onConfirmKeydown);

    // Coarse-pointer tap-to-reveal for the moderator rails (see moderatorActionsTemplate's
    // comment): a single document-level listener, rather than one per row, since rows are cloned
    // in the hundreds over a session. click, never touchstart/pointerdown - those fire at
    // scroll-gesture start and would pop a rail open mid-scroll - and no preventDefault, so a tap on
    // a link inside a row still navigates normally.
    this.onDocumentClick = (event) => {
      if (!this.moderatorValue || !this.coarsePointer) return;
      // Cancel/Accept/backdrop already close the confirm dialog and restore focus to whichever rail
      // button opened it (see closeConfirm) before this bubbles up - by then pendingConfirm is
      // already null, so without this bail hideRailsExcept below would immediately blur and hide the
      // rail that focus was just restored to.
      if (event.target.closest('[data-chat-panel-target="confirm"]')) return;
      if (this.pendingConfirm) return;

      const row = event.target.closest("[data-message-id], [data-tip-id]");
      const rail = row && row.querySelector("[data-chat-rail]");

      // Always hide every other rail, live-queried (never cached - see hideRailsExcept), even when
      // this tap didn't land on a row at all (outside-tap close).
      this.hideRailsExcept(rail);

      if (!rail) return;
      if (event.target.closest("[data-chat-rail]")) return;
      if (event.target.closest("a")) return;

      this.setRailRevealed(rail, true);
    };
    document.addEventListener("click", this.onDocumentClick);

    // The announcement chevron only appears when the 2-line-clamped text overflows;
    // re-check on resize because the chat panel width changes across breakpoints.
    this.onResize = () => this.scheduleAnnouncementToggleRefresh();
    window.addEventListener("resize", this.onResize);
    this.scheduleAnnouncementToggleRefresh();

    // Keep pinnedToBottom current as the reader scrolls, so a later resize (e.g. the mobile
    // keyboard opening/closing) knows whether it should re-pin.
    this.onMessagesScroll = () => { this.pinnedToBottom = this.isPinnedToBottom(); };
    this.messagesTarget.addEventListener("scroll", this.onMessagesScroll, { passive: true });

    // The visual viewport (not window) resizes when the on-screen keyboard opens/closes on
    // mobile; re-pin to the bottom across that resize so a pinned reader never loses their place.
    this.onViewportResize = () => {
      if (this.pinnedToBottom) {
        window.requestAnimationFrame(() => {
          if (this.hasMessagesTarget) this.scrollToBottom();
        });
      }
    };
    if (window.visualViewport) window.visualViewport.addEventListener("resize", this.onViewportResize);

    // A Turbo-restored page can have a pre-filled input, so render a correct counter immediately.
    this.updateCounter();

    this.resetNotices();
    this.startRealtime();
  }

  // The panel is data-turbo-permanent, so its notices outlive a controller instance; a remount
  // starts from the room's current state, not from whatever the previous instance last showed.
  resetNotices() {
    if (this.hasClosedTarget) this.closedTarget.classList.add("hidden");
    if (this.hasTimedOutTarget) this.timedOutTarget.classList.add("hidden");
    if (this.hasDisconnectedTarget) this.setNoticeVisible(this.disconnectedTarget, false);
    if (this.hasReconnectingTarget) this.setTextNoticeVisible(this.reconnectingTarget, false);
    if (this.hasSlowModeTarget) this.setTextNoticeVisible(this.slowModeTarget, false);
  }

  disconnect() {
    this.stopRealtime();
    if (this.hintTimeout) window.clearTimeout(this.hintTimeout);
    if (this.onResize) window.removeEventListener("resize", this.onResize);
    if (this.onMessagesScroll && this.hasMessagesTarget) this.messagesTarget.removeEventListener("scroll", this.onMessagesScroll);
    if (window.visualViewport && this.onViewportResize) window.visualViewport.removeEventListener("resize", this.onViewportResize);
    if (this.onConfirmKeydown) document.removeEventListener("keydown", this.onConfirmKeydown);
    if (this.onDocumentClick) document.removeEventListener("click", this.onDocumentClick);
  }

  // --- realtime -------------------------------------------------------------

  // Acquires the shared room client for this event and subscribes to its frames. The client is
  // keyed by context and survives a panel remount for a moment (see its release grace), so after
  // the go-live Turbo refresh this re-attaches to the live socket: the last bootstrap is replayed
  // from memory and connect() is a no-op, i.e. no new session and no new token.
  startRealtime() {
    this.room = LiveRoomClient.acquire({
      contextType: this.contextTypeValue,
      contextId: this.contextIdValue,
      endpoints: this.endpointsValue,
      heartbeatIntervalMs: this.heartbeatIntervalMsValue,
    });
    this.room.on("bootstrap", (bootstrap) => this.applyBootstrap(bootstrap));
    this.room.on("state", (state) => this.applyRoomState(state));
    this.room.on("message", (frame) => this.received(frame));
    this.room.on("event", (frame) => this.received(frame));
    this.room.on("remove", (frame) => this.received(frame));
    this.room.on("gaveUp", () => this.handleGaveUp());
    this.room.on("closed", ({ reason }) => this.handleRoomClientClosed(reason));

    this.adoptOrphanedPendingRows();
    this.applyRoomState(this.room.state);
    this.room.connect();
    // The replay waits one frame: Stimulus connects this root before the poll card and the tip
    // rail inside it, and their @window actions are not bound yet when connect() runs. The
    // cached bootstrap is kept current by the room client as events arrive, so it restores the
    // room as it is now; time-bound fields are aged by how long ago it landed.
    this.replayFrame = window.requestAnimationFrame(() => {
      this.replayFrame = null;
      if (!this.room || !this.room.lastBootstrap) return;
      this.applyBootstrap(this.room.lastBootstrap, { ageMs: this.room.bootstrapAgeMs });
    });
  }

  // Shared by disconnect() and handleRoomClosed(): releases the room client (it disposes itself
  // once nothing holds it) and stops the timeout ticker. Both are terminal for this controller
  // instance.
  stopRealtime() {
    if (this.replayFrame) window.cancelAnimationFrame(this.replayFrame);
    this.replayFrame = null;
    if (this.room) {
      this.room.release();
      this.room = null;
    }
    if (this.timeoutTimer) {
      window.clearInterval(this.timeoutTimer);
      this.timeoutTimer = null;
    }
    if (this.slowModeTimer) {
      window.clearInterval(this.slowModeTimer);
      this.slowModeTimer = null;
    }
    this.haltAllPending();
  }

  // Nothing that stops the transport leaves it in a state where an automatic resend could ever
  // land, so every still-pending message is halted and surfaced as failed (with its usual retry
  // affordance) rather than left stuck at "Sending..." forever.
  haltAllPending() {
    this.pendingMessages.forEach((pending) => {
      if (pending.timer) window.clearTimeout(pending.timer);
      pending.timer = null;
      if (!pending.failed) {
        pending.failed = true;
        this.setPendingRowState(pending.row, "failed");
      }
    });
    this.updateSendFailedNotice();
  }

  // Pending rows are DOM the panel keeps across a permanent-element remount, but their map entries
  // died with the previous controller instance. Re-adopt them as failed (the body is right there in
  // the row) so the reader still sees them and Retry still works.
  adoptOrphanedPendingRows() {
    this.messagesTarget.querySelectorAll("[data-pending-client-msg-id]").forEach((row) => {
      const clientMsgId = row.dataset.pendingClientMsgId;
      if (this.pendingMessages.has(clientMsgId)) return;

      const bodyEl = row.querySelector("[data-chat-message-body]");
      this.pendingMessages.set(clientMsgId, { body: bodyEl ? bodyEl.textContent : "", row, timer: null, resent: false, failed: true });
      this.setPendingRowState(row, "failed");
    });
    this.updateSendFailedNotice();
  }

  // The room has closed (live-pages--show#closeRoom, either kind, or the server saying so) - wind
  // the live parts of the panel down in place.
  handleRoomClosed() {
    this.stopRealtime();
    this.setInputDisabled(true);
    this.closed = true;
    if (this.hasDisconnectedTarget) this.setNoticeVisible(this.disconnectedTarget, false);
    if (this.hasReconnectingTarget) this.setTextNoticeVisible(this.reconnectingTarget, false);
    if (this.hasSlowModeTarget) this.setTextNoticeVisible(this.slowModeTarget, false);
    if (this.hasClosedTarget) this.closedTarget.classList.remove("hidden");
  }

  // The room client stopped for good: 401 means the session is gone (sign in again, or reload);
  // 403 and 410 mean this viewer may no longer join, which reads as "chat is closed".
  handleRoomClientClosed(reason) {
    if (reason === "unauthenticated") {
      this.handleGaveUp();
    } else {
      this.handleRoomClosed();
    }
  }

  // Connection state, from the room client. The composer is usable only while connected; the
  // "Reconnecting" notice shows while a connection is being re-established or waited for, but not
  // during the silent first connect.
  applyRoomState(state) {
    if (this.closed) return;

    const connected = state === "connected";
    if (connected) this.everConnected = true;
    const reconnecting = state === "backoff" || (state === "connecting" && this.everConnected);
    if (this.hasReconnectingTarget) this.setTextNoticeVisible(this.reconnectingTarget, reconnecting);

    if (connected) {
      if (this.hasDisconnectedTarget) this.setNoticeVisible(this.disconnectedTarget, false);
      this.enableInput();
      this.resendPendingMessages();
    } else {
      this.setInputDisabled(true);
    }
  }

  // The client exhausted its reconnect cycles and stopped; the reader gets a Reconnect button.
  handleGaveUp() {
    if (this.closed) return;

    this.setInputDisabled(true);
    this.haltAllPending();
    if (this.hasReconnectingTarget) this.setTextNoticeVisible(this.reconnectingTarget, false);
    if (this.hasDisconnectedTarget) this.setNoticeVisible(this.disconnectedTarget, true);
  }

  // Wired from the disconnected notice's retry button. The composer stays disabled until the
  // room client reports connected again. A client that closed on 401 is terminal, so it is
  // released and a fresh one acquired: that starts a new session, which succeeds once the viewer
  // has signed back in (from another tab, say) and shows this notice again if they have not.
  reconnect() {
    if (this.hasDisconnectedTarget) this.setNoticeVisible(this.disconnectedTarget, false);
    if (this.room && this.room.state !== "closed") {
      this.room.connect();
      return;
    }
    if (this.room) this.room.release();
    this.room = null;
    this.startRealtime();
  }

  // The bootstrap state the session endpoint returns on every (re)connect. Idempotent - it is
  // applied again on each token handover and on a permanent-element remount.
  applyBootstrap(bootstrap, { ageMs = 0 } = {}) {
    this.viewer = bootstrap.viewer || {};
    const room = bootstrap.room || {};
    const state = bootstrap.state || {};

    if (state.viewers != null) this.updateViewerCount(state.viewers);
    const announcement = room.announcement;
    this.setAnnouncement(announcement ? announcement.body : "", announcement ? announcement.body_html : null);

    if (state.poll && state.poll.poll) {
      this.received({ type: "poll", action: "sync", poll: state.poll.poll, your_option_id: state.poll.your_option_id });
    }
    // Pinned tips (latest-first) for the pinned tip rail only; the inline scroll does not replay past tips.
    this.dispatch("tips-backfill", { detail: state.tips || [], target: window });
    // History is a nicety: nothing wrong with it may skip the slow mode, timeout and closed checks below.
    try {
      this.renderHistory();
    } catch (error) {
      window.setTimeout(() => { throw error; });
    }

    this.applySlowModeSetting(room.slow_mode_seconds);
    const timedOutSeconds = Number(this.viewer.timed_out_seconds) - Math.floor(ageMs / 1000);
    if (timedOutSeconds > 0) {
      this.handleTimedOut(timedOutSeconds, this.viewer.timeout_reason, this.viewer.timed_out_by);
    }

    if (room.closes_at && Date.parse(room.closes_at) <= Date.now()) this.handleRoomClosed();
  }

  // The room's recent messages as this tab holds them: the room client folds each session's list
  // into its buffer, so a replayed older bootstrap still renders the room as it is now. Rows are
  // only ever added here, never removed.
  renderHistory() {
    if (!this.room) return;

    const pinned = this.isPinnedToBottom();
    this.insertHistory(this.room.history);
    if (pinned) this.scrollToBottom();
  }

  // Only what is not on screen yet, which keeps a remount or a new session idempotent, each placed
  // by send time among the rows that carry one. Our own sends the history has seen confirm their
  // pending rows first, where they stand, so the rest place around them. Both lists are in send
  // order, so one pass places them all.
  insertHistory(messages) {
    const fresh = messages.filter((message) => {
      if (!this.pendingMessages.has(message.client_msg_id)) return true;
      this.received(message);
      return false;
    });

    const ids = new Set();
    const clientIds = new Set();
    this.messagesTarget.querySelectorAll(":scope > [data-message-id]").forEach((row) => ids.add(row.dataset.messageId));
    this.messagesTarget.querySelectorAll(":scope > [data-client-msg-id]").forEach((row) => clientIds.add(row.dataset.clientMsgId));
    const rows = Array.from(this.messagesTarget.querySelectorAll(":scope > [data-at]"));
    const times = rows.map((row) => Date.parse(row.dataset.at));
    const tail = this.historyTail(rows);

    let next = 0;
    fresh.forEach((message) => {
      if (ids.has(String(message.id)) || (message.client_msg_id != null && clientIds.has(String(message.client_msg_id)))) return;

      const at = Date.parse(message.at);
      while (next < rows.length && times[next] <= at) next += 1;
      this.messagesTarget.insertBefore(this.buildMessage(message), rows[next] || tail);
    });
  }

  // Where a message newer than every stamped row goes: after them all, but above the viewer's own
  // still-sending row, which stays last until its echo confirms it.
  historyTail(rows) {
    let node = rows.length ? rows[rows.length - 1].nextElementSibling : this.messagesTarget.firstElementChild;
    while (node && !node.hasAttribute("data-pending-client-msg-id")) node = node.nextElementSibling;
    return node;
  }

  // Shared hidden/flex visibility toggle for the icon-bearing notices (disconnected, failed send),
  // matching the classList.toggle idiom updateSendFailedNotice already uses.
  setNoticeVisible(target, visible) {
    target.classList.toggle("hidden", !visible);
    target.classList.toggle("flex", visible);
  }

  // The plain-text notices (reconnecting, slow mode) render like hint: block text, never flex,
  // which would collapse the space before the countdown span.
  setTextNoticeVisible(target, visible) {
    target.classList.toggle("hidden", !visible);
  }

  // Every frame the room client emits: room messages and deletes from the SDK, and the server's
  // room events (poll, tip, announcement, user_removed, viewers), each already normalized to the
  // shape this switch has always consumed.
  received(data) {
    switch (data.type) {
      case "message": {
        // Our own message echoed back always scrolls into view, even if the reader had scrolled
        // up before sending. `pinned` must be computed here, before the DOM below changes
        // scrollHeight - isPinnedToBottom() afterwards would no longer reflect pre-send state.
        const pending = this.pendingMessages.get(data.client_msg_id);
        const mine = !!pending;
        const pinned = mine || this.isPinnedToBottom();
        if (pending && pending.row.isConnected) {
          // Our echo confirms the optimistic row where it stands rather than re-appending it.
          this.confirmPendingRow(pending.row, data);
          this.resolvePendingMessage(data.client_msg_id, pending, { keepRow: true });
        } else {
          // appendMessage() first: if resolving the pending entry below throws, the real message
          // has already landed rather than being lost.
          this.appendMessage(data);
          if (pending) this.resolvePendingMessage(data.client_msg_id, pending);
        }
        if (pinned) this.scrollToBottom();
        break;
      }
      case "remove":
        this.removeMessage(data.message_id);
        break;
      case "user_removed":
        this.handleRemoveUser(data.user_id);
        break;
      case "viewers":
        this.updateViewerCount(data.count);
        break;
      case "announcement":
        this.setAnnouncement(data.body, data.body_html);
        break;
      case "settings":
        this.applySlowModeSetting(data.slow_mode_seconds);
        break;
      case "stream_started":
      case "stream_ended":
        this.refreshPageForStreamEvent();
        break;
      case "poll":
        // Forward poll events to the floating card and the console's poll column (window event so
        // either may be absent), stamped with the event they belong to.
        this.dispatchPoll(data);
        break;
      case "tip":
        // A tip renders in BOTH places (deduped by id): highlighted inline AND in the pinned tip
        // rail. The rail may be absent (dashboard / flag off), so forward via a window event.
        if (data.action === "created") {
          this.addInlineTip(data.tip);
          this.dispatch("tip", { detail: data.tip, target: window });
        } else if (data.action === "removed") {
          const id = data.tip && data.tip.id;
          this.removeInlineTip(id);
          this.dispatch("tip-removed", { detail: { id }, target: window });
        }
        break;
    }
  }

  // Count renders twice (mobile span + desktop pill, both outside this controller's element) -
  // a single querySelector only ever hit the first (mobile) node, freezing the other viewport's.
  updateViewerCount(count) {
    document.querySelectorAll("[data-live-viewer-count]").forEach((el) => {
      el.setAttribute("data-rolling-number-count-value", count);
    });
  }

  // Cast a vote for the floating card (it has no endpoint of its own). The server acks to this
  // voter alone in the response, so the ack/rejection is forwarded to the card the same way a
  // room-wide poll frame is.
  castVote(event) {
    if (!this.endpointsValue.votes) return;

    const pollId = event.detail.pollId;
    const optionId = event.detail.optionId;
    postJson(this.endpointsValue.votes, { ...this.contextParams(), poll_id: pollId, option_id: optionId })
      .then((json) => {
        if (json && json.action === "vote_ack" && this.room) this.room.noteVote(json.poll_id, json.option_id);
        this.dispatchPoll(json);
      })
      .catch((error) => {
        const payload = error && error.payload;
        const detail = payload && payload.type === "poll"
          ? payload
          : { type: "poll", action: "vote_rejected", poll_id: pollId, reason: "Your vote couldn't be recorded." };
        this.dispatchPoll(detail);
      });
  }

  dispatchPoll(data) {
    this.dispatch("poll", { detail: { ...data, live_event_id: this.contextIdValue }, target: window });
  }

  setAnnouncement(body, bodyHtml) {
    if (!this.hasAnnouncementTarget) return;

    const text = body || "";
    // body_html is server-escaped + linkified, so it's safe to inject; fall back to plain text if the payload omits it.
    if (bodyHtml != null) {
      this.announcementBodyTarget.innerHTML = bodyHtml;
    } else {
      this.announcementBodyTarget.textContent = text;
    }
    // This is the single authority on what the banner reads, including for the creator who just
    // typed it: LiveEventsController#update publishes on save, so the inline editor never writes
    // the text itself and the linkified markup can't be raced away by a plain-text local write.
    const blank = text.trim() === "";
    this.announcementBodyTarget.hidden = blank;
    if (this.hasAnnouncementPlaceholderTarget) this.announcementPlaceholderTarget.hidden = !blank;

    this.announcementTarget.classList.toggle("hidden", blank && !this.announcementEditableValue);
    this.collapseAnnouncement();
    this.scheduleAnnouncementToggleRefresh();
  }

  toggleAnnouncement() {
    this.announcementExpanded = !this.announcementExpanded;
    this.announcementTextTarget.classList.toggle("line-clamp-2", !this.announcementExpanded);
    this.announcementToggleTarget.classList.toggle("rotate-180", this.announcementExpanded);
    this.announcementToggleTarget.setAttribute("aria-expanded", String(this.announcementExpanded));
  }

  collapseAnnouncement() {
    this.announcementExpanded = false;
    if (this.hasAnnouncementTextTarget) this.announcementTextTarget.classList.add("line-clamp-2");
    if (this.hasAnnouncementToggleTarget) {
      this.announcementToggleTarget.classList.remove("rotate-180");
      this.announcementToggleTarget.setAttribute("aria-expanded", "false");
    }
  }

  scheduleAnnouncementToggleRefresh() {
    window.requestAnimationFrame(() => this.refreshAnnouncementToggle());
  }

  // Show the expand chevron only when the clamped text overflows two lines. While
  // expanded the chevron stays (it's the collapse control), so only measure when collapsed.
  refreshAnnouncementToggle() {
    if (!this.hasAnnouncementToggleTarget || !this.hasAnnouncementTextTarget) return;
    if (this.announcementExpanded) return;

    const el = this.announcementTextTarget;
    const overflowing = el.scrollHeight > el.clientHeight + 1;
    this.announcementToggleTarget.classList.toggle("hidden", !overflowing);
  }

  // --- sending ----------------------------------------------------------------

  send(event) {
    event.preventDefault();

    const body = this.inputTarget.value.trim();
    if (body === "") return;

    // Defense in depth: setInputDisabled normally keeps this unreachable (handleRoomClosed
    // releases the room in the same breath as disabling the composer), but a missing room must
    // never reach deliver() below - the typed message would vanish with no feedback at all.
    if (!this.room) {
      this.showHint("Chat isn't connected. Reload the page to send your message.");
      return;
    }

    // Generated once per message, even across a reconnect resend or a manual retry: the same id is
    // tracked in pendingMessages (so received() can recognize our own echo) and travels in the
    // message attributes as client_msg_id, so a resend reusing it lets each client's own
    // appendMessage() DOM-presence check absorb the duplicate echo of a message that actually did
    // arrive the first time - a fresh id on resend would double-post it instead. The SDK keeps its
    // own per-attempt requestId underneath; there is no server-side dedupe.
    const clientMsgId = this.newClientMsgId();
    const row = this.buildPendingMessage(clientMsgId, body);
    this.messagesTarget.appendChild(row);
    // Our own outgoing message always scrolls into view, same as its echo does in received().
    this.scrollToBottom();

    this.pendingMessages.set(clientMsgId, { body, row, timer: null, resent: false, failed: false });
    this.deliver(clientMsgId);
    this.applySlowModeLock();

    this.clearInput();
    this.focusInput();
  }

  // Hands one pending message to the room and arms its settle timer. Shared by send(), the
  // reconnect resend sweep and retrySend(). The echo normally confirms it through the message
  // listener before the promise settles; a resolved promise with no echo yet synthesises the row
  // from the resolved ChatMessage, and a rejection maps the SDK's ChatError code to an outcome.
  deliver(clientMsgId) {
    const pending = this.pendingMessages.get(clientMsgId);
    if (!pending || !this.room) return;

    this.startPendingTimer(clientMsgId);
    if (this.viewer && this.viewer.shadow) {
      this.simulateEcho(clientMsgId, pending.body);
      return;
    }
    this.room.send(pending.body, { clientMsgId })
      .then((message) => this.onSendResolved(clientMsgId, message))
      .catch((error) => this.onSendRejected(clientMsgId, error));
  }

  // A shadow-banned viewer holds a read-only token, so their own page fakes the echo through the
  // very same received() path (same row, same scroll behaviour) and nothing reaches the room.
  // Their view never changes, which is the whole point of a shadow ban.
  simulateEcho(clientMsgId, body) {
    window.setTimeout(() => {
      if (!this.pendingMessages.has(clientMsgId)) return;
      this.receivedOwn({
        type: "message",
        id: `${SHADOW_ID_PREFIX}${clientMsgId}`,
        client_msg_id: clientMsgId,
        user: this.viewer.identity,
        body,
        at: new Date().toISOString(),
      });
    }, SHADOW_ECHO_MIN_MS + Math.random() * SHADOW_ECHO_SPREAD_MS);
  }

  onSendResolved(clientMsgId, chatMessage) {
    if (!this.pendingMessages.has(clientMsgId)) return;
    this.receivedOwn(normalizeMessage(chatMessage));
  }

  // A message frame this panel made rather than one from the room, so the room client's history
  // buffer only hears of it from here - for a shadow echo, the only record there will ever be.
  receivedOwn(frame) {
    if (this.room) this.room.noteMessage(frame);
    this.received(frame);
  }

  // ChatError codes from the IVS messaging API (see chatErrorCodes in the SDK). 401 is a token
  // that expired mid-flight: the SDK reconnects on its own and the connect resend picks the
  // message up, so it stays pending. A synchronous throw (not connected) has no code and fails.
  onSendRejected(clientMsgId, error) {
    if (!this.pendingMessages.has(clientMsgId)) return;

    const code = error && typeof error.errorCode === "number" ? error.errorCode : null;
    switch (code) {
      case 429:
        this.failMessage(clientMsgId);
        this.showHint("You're sending too fast. Try again in a moment.");
        break;
      case 400:
      case 413:
        this.cancelPendingMessage(clientMsgId);
        this.showHint("That message is too long.");
        break;
      case 403:
        // The token no longer carries SEND_MESSAGE (a timeout or ban landed since it was minted).
        // The row stays failed with its Retry, so the typed text is not lost, and the refresh that
        // fetches the bootstrap saying what this viewer is now is rate-limited like any other.
        this.failMessage(clientMsgId);
        this.showHint("You can't send messages right now.");
        this.refreshRoomThrottled();
        break;
      case 401:
        break;
      default:
        this.failMessage(clientMsgId);
    }
  }

  startPendingTimer(clientMsgId) {
    const pending = this.pendingMessages.get(clientMsgId);
    if (!pending) return;
    if (pending.timer) window.clearTimeout(pending.timer);
    pending.timer = window.setTimeout(() => this.handleSendTimeout(clientMsgId), SEND_TIMEOUT_MS);
  }

  // Nothing settled the send in time: surface it as failed and, rate-limited, cycle the socket - a
  // half-open connection still reports connected, and a new connection is the only way to find out.
  handleSendTimeout(clientMsgId) {
    if (!this.pendingMessages.has(clientMsgId)) return;

    this.failMessage(clientMsgId);
    this.refreshRoomThrottled();
  }

  refreshRoomThrottled() {
    if (!this.room) return;

    const now = Date.now();
    if (now - this.lastRefreshAt < REFRESH_MIN_INTERVAL_MS) return;
    this.lastRefreshAt = now;
    this.room.refresh();
  }

  // Runs on every connect (a reconnect or otherwise): resend whatever is still pending, once, on
  // the new connection, reusing each id so a message that did land the first time is absorbed by
  // appendMessage()'s dedupe rather than double-posted.
  resendPendingMessages() {
    this.pendingMessages.forEach((pending, clientMsgId) => {
      if (pending.failed || pending.resent) return;
      pending.resent = true;
      this.deliver(clientMsgId);
    });
  }

  // The echo arrived: the message is confirmed. The row goes unless it was confirmed in place.
  resolvePendingMessage(clientMsgId, pending, { keepRow = false } = {}) {
    if (pending.timer) window.clearTimeout(pending.timer);
    this.pendingMessages.delete(clientMsgId);
    if (!keepRow) pending.row.remove();
    this.updateSendFailedNotice();
  }

  // A rejection, not a delivery or a timeout: just stop waiting, with no confirmed row and no
  // retry affordance. The slow-mode lock is lifted too - nothing was sent.
  cancelPendingMessage(clientMsgId) {
    const pending = this.pendingMessages.get(clientMsgId);
    if (!pending) return;

    if (pending.timer) window.clearTimeout(pending.timer);
    this.pendingMessages.delete(clientMsgId);
    pending.row.remove();
    this.updateSendFailedNotice();
    this.clearSlowModeLock();
  }

  // Stop retrying automatically and surface a manual retry instead. A failed send lifts the
  // slow-mode lock so the retry is not held behind it.
  failMessage(clientMsgId) {
    const pending = this.pendingMessages.get(clientMsgId);
    if (!pending) return;

    if (pending.timer) window.clearTimeout(pending.timer);
    pending.timer = null;
    pending.failed = true;
    this.setPendingRowState(pending.row, "failed");
    this.updateSendFailedNotice();
    this.clearSlowModeLock();
  }

  // Wired from both the in-row retry control (which passes its own client_msg_id) and the
  // composer-adjacent failed-send notice (which has none, so it retries every failed message).
  retryMessage(event) {
    const clientMsgId = event.params && event.params.clientMsgId;
    const ids = clientMsgId
      ? [clientMsgId]
      : Array.from(this.pendingMessages.keys()).filter((id) => this.pendingMessages.get(id).failed);

    ids.forEach((id) => this.retrySend(id));
  }

  retrySend(clientMsgId) {
    const pending = this.pendingMessages.get(clientMsgId);
    if (!pending) return;

    if (!this.room) {
      this.showHint("Chat isn't connected. Reload the page to send your message.");
      return;
    }

    pending.failed = false;
    pending.resent = false;
    this.setPendingRowState(pending.row, "pending");
    this.updateSendFailedNotice();
    this.deliver(clientMsgId);
  }

  contextParams() {
    return { context_type: this.contextTypeValue, context_id: this.contextIdValue };
  }

  // The viewer page re-renders itself at go-live and at stream end (player, title, copy) when the
  // stream_started / stream_ended room events arrive. Jittered so an audience does not reload
  // together; the panel is data-turbo-permanent and the room client outlives the swap, so no new
  // token is minted.
  refreshPageForStreamEvent() {
    if (!this.refreshOnStreamEventsValue) return;
    if (!window.Turbo || !window.Turbo.session) return;

    window.setTimeout(() => window.Turbo.session.refresh(window.location.href), jitter(0, STREAM_EVENT_REFRESH_SPREAD_MS));
  }

  // --- slow mode -------------------------------------------------------------------
  // Enforced here, from the bootstrap and the settings event (the room-wide rate cap is the only
  // hard limit); moderators are exempt. The lock is taken on send and lifted by the countdown, by
  // a failed send, or by the creator turning slow mode off.

  applySlowModeSetting(seconds) {
    this.slowModeSeconds = Math.max(0, Number(seconds) || 0);
    this.updateSlowModeBadge();
    if (this.slowModeSeconds === 0) this.clearSlowModeLock();
  }

  applySlowModeLock() {
    if (this.slowModeSeconds <= 0 || this.moderatorValue) return;

    this.slowModeUntil = Date.now() + this.slowModeSeconds * 1000;
    this.slowModeLocked = true;
    this.setInputDisabled(true);
    if (this.hasSlowModeTarget) this.setTextNoticeVisible(this.slowModeTarget, true);
    if (this.slowModeTimer) window.clearInterval(this.slowModeTimer);
    this.tickSlowMode();
    this.slowModeTimer = window.setInterval(() => this.tickSlowMode(), 1000);
  }

  tickSlowMode() {
    const result = countdown(new Date(this.slowModeUntil).toISOString());
    const remaining = result ? result.remaining : 0;

    if (this.hasSlowModeCountdownTarget) this.slowModeCountdownTarget.textContent = result ? result.label : "0:00";
    if (remaining > 0) return;

    this.clearSlowModeLock();
  }

  clearSlowModeLock() {
    if (this.slowModeTimer) window.clearInterval(this.slowModeTimer);
    this.slowModeTimer = null;
    this.slowModeUntil = 0;
    if (!this.slowModeLocked) return;

    this.slowModeLocked = false;
    if (this.hasSlowModeTarget) this.setTextNoticeVisible(this.slowModeTarget, false);
    this.enableInput();
  }

  // The badge sits in the page header, outside this element, so it is reached by hook: hidden at
  // 0, and its styled tooltip (the data-tooltip attribute) rewritten to the current setting with
  // the same copy Element::SlowModeBadgeComponent renders.
  updateSlowModeBadge() {
    const seconds = this.slowModeSeconds;
    const copy = `Chat is limited to one message every ${seconds} ${seconds === 1 ? "second" : "seconds"} per person.`;
    document.querySelectorAll("[data-live-slow-mode-badge]").forEach((badge) => {
      badge.hidden = seconds === 0;
      const tooltip = badge.querySelector("[data-live-slow-mode-tooltip]");
      if (tooltip) tooltip.setAttribute("data-tooltip", copy);
    });
  }

  // The optimistic row IS the final row: messageTemplate filled with the sender's own identity
  // from the bootstrap (avatar, name, badge), dimmed and with the status beside the name, so
  // confirming it later moves nothing on screen. Marked with data-pending-client-msg-id (never
  // data-client-msg-id or data-message-id - appendMessage()'s dedupe and the moderator rail match
  // those, and colliding here would suppress the authoritative row or offer moderation on a
  // message that does not exist yet).
  buildPendingMessage(clientMsgId, body) {
    const identity = (this.viewer && this.viewer.identity) || { id: this.currentUserIdValue, name: "" };
    const wrapper = this.buildMessage({ id: null, client_msg_id: null, user: identity, body }, { actions: false });
    wrapper.dataset.pendingClientMsgId = clientMsgId;
    wrapper.classList.add("opacity-60");

    const status = this.pendingStatusTemplateEl.content.cloneNode(true).firstElementChild;
    status.querySelector("[data-chat-pending-retry-button]").dataset.chatPanelClientMsgIdParam = clientMsgId;
    // Beside the name, not under the body: the identity line keeps its height when it goes.
    wrapper.querySelector("[data-chat-message-identity]").appendChild(status);
    return wrapper;
  }

  // The echo (or the resolved send) confirms the optimistic row where it stands: it gains the
  // IVS message id, loses the dim and the status line, and a moderator gets the rail.
  confirmPendingRow(row, message) {
    row.dataset.messageId = message.id;
    if (message.client_msg_id) row.dataset.clientMsgId = message.client_msg_id;
    if (message.at) row.dataset.at = message.at;
    delete row.dataset.pendingClientMsgId;
    row.classList.remove("opacity-60");
    const status = row.querySelector("[data-chat-pending-line]");
    if (status) status.remove();
    if (this.moderatorValue && this.moderatorActionsTemplateEl) row.appendChild(this.buildModeratorActions(message));
  }

  setPendingRowState(row, state) {
    const failed = state === "failed";
    row.classList.toggle("opacity-60", !failed);

    row.querySelector("[data-chat-pending-status]").classList.toggle("hidden", failed);

    const failedEl = row.querySelector("[data-chat-pending-failed]");
    failedEl.classList.toggle("hidden", !failed);
    failedEl.classList.toggle("flex", failed);
  }

  updateSendFailedNotice() {
    if (!this.hasSendFailedNoticeTarget) return;

    const anyFailed = Array.from(this.pendingMessages.values()).some((pending) => pending.failed);
    this.sendFailedNoticeTarget.classList.toggle("hidden", !anyFailed);
    this.sendFailedNoticeTarget.classList.toggle("flex", anyFailed);
  }

  // Keeps the on-screen keyboard open on mobile across a send, since blurring the input closes it.
  focusInput() {
    if (this.hasInputTarget && !this.inputTarget.disabled) this.inputTarget.focus();
  }

  // Clears the input programmatically (a plain value reset fires no `input` event, so the
  // counter is updated explicitly here). Called both from send() above and, cross-controller,
  // from chat-tip's onSucceeded() after a successful tip.
  clearInput() {
    this.inputTarget.value = "";
    this.updateCounter();
  }

  // Shows "<length>/<max>" only once the message is within COUNTER_VISIBLE_REMAINING characters
  // of the input's maxlength (read from the DOM so the limit is never declared twice on the
  // client). max <= 0 means no maxlength attribute is set (browsers report -1), which would
  // otherwise satisfy the threshold immediately.
  updateCounter() {
    if (!this.hasInputTarget || !this.hasCounterTarget) return;

    const max = this.inputTarget.maxLength;
    if (max <= 0) return;

    const length = this.inputTarget.value.length;
    const remaining = max - length;
    this.counterTarget.classList.toggle("hidden", remaining > COUNTER_VISIBLE_REMAINING);
    this.counterTarget.textContent = `${length}/${max}`;
  }

  // --- moderation ---------------------------------------------------------------
  // Every action is a server call (the token carries no moderation capability), and none of them
  // touches the DOM locally: the room's authoritative remove / user_removed / tip events do that,
  // for this moderator and everyone else alike.

  deleteMessage(event) {
    if (!this.moderatorValue) return;

    const messageId = event.params.messageId;
    const row = this.rowFor(event);
    this.openConfirm({
      variant: "delete",
      preview: row ? this.previewFromRow(row) : null,
      trigger: event.currentTarget,
      run: () => deleteJson(`${this.endpointsValue.messages}/${encodeURIComponent(messageId)}`, this.contextParams()),
    });
  }

  banUser(event) {
    if (!this.moderatorValue) return;

    const userId = event.params.userId;
    const row = this.rowFor(event);
    this.openConfirm({
      variant: "ban",
      preview: row ? this.previewFromRow(row) : null,
      trigger: event.currentTarget,
      run: () => postJson(this.endpointsValue.bans, { ...this.contextParams(), user_id: userId }),
    });
  }

  // Moderator time-out from the rail's "Time out" action. Unlike ban/delete, `run` reads two more
  // pieces of state live at accept time - the selected duration chip and the reason textarea - both
  // of which the user can only have set after this closure was built (openConfirm resets them, then
  // the dialog sits open for the moderator to change them, then acceptConfirm calls this closure).
  timeOutUser(event) {
    if (!this.moderatorValue) return;

    const userId = event.params.userId;
    const row = this.rowFor(event);
    this.openConfirm({
      variant: "timeout",
      preview: row ? this.previewFromRow(row) : null,
      trigger: event.currentTarget,
      run: () => postJson(this.endpointsValue.timeouts, {
        ...this.contextParams(),
        user_id: userId,
        seconds: this.selectedTimeoutSeconds,
        reason: this.hasTimeoutReasonTarget ? this.timeoutReasonTarget.value.trim() : "",
      }),
    });
  }

  // Moderator remove from the inline tip's toolbar (params-based, like deleteMessage).
  removeTip(event) {
    if (!this.moderatorValue) return;

    const row = this.rowFor(event);
    this.confirmRemoveTip(event.params.tipId, { trigger: event.currentTarget, preview: row ? this.previewFromRow(row) : null });
  }

  // Moderator remove from the pinned tip rail. The rail owns no endpoints, so it asks us (which does) via a window event.
  removeRailTip(event) {
    if (!this.moderatorValue) return;

    const detail = event.detail || {};
    this.confirmRemoveTip(detail.id, { trigger: detail.trigger || null, preview: detail.tip ? this.previewFromTip(detail.tip) : null });
  }

  // Shared by both tip removal paths: the server unpins + removes it for everyone; no refund.
  confirmRemoveTip(tipId, { trigger, preview } = {}) {
    if (tipId == null) return;

    this.openConfirm({
      variant: "tip",
      preview,
      trigger,
      run: () => deleteJson(`${this.endpointsValue.tips}/${encodeURIComponent(tipId)}`, this.contextParams()),
    });
  }

  openConfirm({ variant, preview, trigger, run }) {
    if (!this.hasConfirmTarget) {
      this.runModeration(run);
      return;
    }
    if (this.pendingConfirm) return;

    this.pendingConfirm = { run };
    this.confirmTrigger = trigger || null;
    this.confirmOpenedAt = Date.now();
    this.showConfirmVariant(variant);
    // Reset on open, never on close: acceptConfirm() closes the dialog before running the pending
    // action, so a close-time reset would wipe the chip/textarea state before that action reads it.
    if (variant === "timeout") this.resetTimeoutFields(preview);
    this.fillConfirmPreview(preview);
    this.confirmTarget.classList.remove("hidden");
    // Cancel takes focus, not Confirm, so a reflex Enter mid-composer can't fire the destructive action.
    this.confirmCancelTarget.focus();
  }

  // Timeout-only reset: the default 10-minute chip, an empty reason, and the target's name filled
  // into the title (there is no server-side name to interpolate - the target is chosen client-side).
  resetTimeoutFields(preview) {
    this.selectedTimeoutSeconds = TIMEOUT_DEFAULT_SECONDS;
    if (this.hasTimeoutReasonTarget) this.timeoutReasonTarget.value = "";
    this.timeoutDurationChipTargets.forEach((chip) => {
      this.setTimeoutChipActive(chip, Number(chip.dataset.chatPanelSecondsParam) === this.selectedTimeoutSeconds);
    });
    if (this.hasConfirmTitleNameTarget) this.confirmTitleNameTarget.textContent = (preview && preview.name) || "this person";
  }

  selectTimeoutDuration(event) {
    const seconds = Number(event.params.seconds);
    if (!TIMEOUT_DURATIONS.includes(seconds)) return;

    this.selectedTimeoutSeconds = seconds;
    this.timeoutDurationChipTargets.forEach((chip) => {
      this.setTimeoutChipActive(chip, Number(chip.dataset.chatPanelSecondsParam) === seconds);
    });
  }

  setTimeoutChipActive(chip, active) {
    chip.setAttribute("aria-pressed", String(active));
    chip.classList.toggle("border-accent", active);
    chip.classList.toggle("bg-accent/10", active);
    chip.classList.toggle("text-accent", active);
    chip.classList.toggle("border-gray-200", !active);
    chip.classList.toggle("text-gray-600", !active);
  }

  acceptConfirm() {
    const pending = this.pendingConfirm;
    this.closeConfirm();

    if (!pending || !this.moderatorValue) return;

    this.runModeration(pending.run);
  }

  // A moderation call that fails says what happened, from the status the endpoint returns on
  // purpose; nothing was changed locally on the way in.
  runModeration(run) {
    Promise.resolve().then(run).catch((error) => this.showHint(this.moderationFailureCopy(error)));
  }

  moderationFailureCopy(error) {
    const status = error && typeof error.status === "number" ? error.status : null;
    const code = error && error.payload && error.payload.error;

    if (status === 0) return "Couldn't reach Supercast. Check your connection and try again.";
    if (status === 503 && code === "disconnect_failed") return "Saved, but they're still connected. Try again to disconnect them.";
    if (status === 503) return "Chat is unavailable right now. Try again in a moment.";
    if (code === "protected") return "Moderators and admins can't be banned or timed out.";
    if (code === "invalid_duration") return "Pick one of the timeout durations.";
    if (status === 404) return "That message or person isn't in this chat any more.";
    if (status === 403) return "You don't have permission to do that.";
    return "Something went wrong. Try again.";
  }

  cancelConfirm() {
    this.closeConfirm();
  }

  confirmBackdropClick(event) {
    if (event.currentTarget !== event.target) return;
    if (Date.now() - this.confirmOpenedAt < CONFIRM_DISMISS_GRACE_MS) return;

    this.cancelConfirm();
  }

  closeConfirm() {
    const trigger = this.confirmTrigger;

    this.pendingConfirm = null;
    this.confirmTrigger = null;
    if (this.hasConfirmTarget) this.confirmTarget.classList.add("hidden");

    // The Tab-trap and the click-intercepting backdrop mean focus cannot legitimately leave the
    // dialog while it's open, so restoring it here doesn't need to reactively check where focus
    // currently sits (which a backdrop click's own mousedown has already blurred to <body>).
    if (trigger && trigger.isConnected) trigger.focus();
  }

  showConfirmVariant(variant) {
    this.confirmTarget.querySelectorAll("[data-chat-confirm-variant]").forEach((el) => {
      el.hidden = el.dataset.chatConfirmVariant !== variant;
    });
  }

  // A live query, not a hardcoded stop list: showConfirmVariant() above is the only thing that
  // decides what's actually showing (it flips `hidden` on whichever [data-chat-confirm-variant]
  // wrapper isn't the active one), so filtering on `!el.closest("[hidden]")` tracks that mechanism
  // directly - the timeout variant's duration chips and reason textarea join the trap automatically
  // while their wrapper is the one left un-hidden, with no separate wiring needed here.
  confirmFocusableElements() {
    const dialog = this.confirmTarget.querySelector('[role="dialog"]');
    if (!dialog) return [];

    return Array.from(dialog.querySelectorAll(CONFIRM_FOCUSABLE_SELECTOR)).filter(
      (el) => !el.disabled && !el.closest("[hidden]")
    );
  }

  fillConfirmPreview(preview) {
    const show = !!preview;
    this.confirmPreviewTarget.classList.toggle("hidden", !show);
    this.confirmPreviewTarget.classList.toggle("flex", show);
    if (!show) return;

    applyAvatar(this.confirmPreviewAvatarTarget, this.confirmPreviewAvatarFallbackTarget, preview.user);
    this.confirmPreviewNameTarget.textContent = preview.name || "";
    this.confirmPreviewBodyTarget.textContent = preview.body || "";

    const amount = preview.amount || "";
    this.confirmPreviewAmountTarget.textContent = amount;
    this.confirmPreviewAmountTarget.hidden = amount === "";
  }

  rowFor(event) {
    return event.currentTarget.closest("[data-message-id], [data-tip-id]");
  }

  previewFromRow(row) {
    const nameEl = row.querySelector("[data-chat-message-name]");
    const bodyEl = row.querySelector("[data-chat-message-body]");
    const amountEl = row.querySelector("[data-chat-tip-amount]");
    const avatarEl = row.querySelector("[data-chat-message-avatar]");
    const fallbackEl = row.querySelector("[data-chat-message-avatar-fallback]");

    return {
      name: nameEl ? nameEl.textContent : "",
      body: bodyEl ? bodyEl.textContent : "",
      amount: amountEl ? amountEl.textContent : "",
      user: {
        avatar_url: avatarEl && !avatarEl.classList.contains("hidden") ? avatarEl.src : null,
        initials: fallbackEl ? fallbackEl.textContent : "",
        avatar_color_class: (fallbackEl && fallbackEl.dataset.avatarColorClass) || null,
      },
    };
  }

  previewFromTip(tip) {
    return {
      name: (tip.user && tip.user.name) || "",
      body: (tip.body || "").trim(),
      amount: this.tipAmountLabel(tip),
      user: tip.user || null,
    };
  }

  // Shared by the inline tip row's badge and the confirm preview, so the two can't format differently.
  tipAmountLabel(tip) {
    return `TIP ${formatMoney(tip.amount_cents, tip.currency)}`;
  }

  // Deduped on both keys: the IVS message id (the SDK's own replay guard covers only a
  // connection switch) and the sender's client_msg_id, which a retry reuses.
  appendMessage(message) {
    if (message.id != null && this.messagesTarget.querySelector(`[data-message-id="${CSS.escape(String(message.id))}"]`)) return;
    if (message.client_msg_id && this.messagesTarget.querySelector(`[data-client-msg-id="${CSS.escape(String(message.client_msg_id))}"]`)) return;

    this.messagesTarget.appendChild(this.buildMessage(message));
  }

  // Clones messageTemplate (declared in the panel markup) and fills its slots. Markup lives
  // in the ERB template; this only ever populates it. actions: false builds a row with no
  // moderator rail (the optimistic row, which has no message id to moderate yet).
  buildMessage(message, { actions = true } = {}) {
    const wrapper = this.messageTemplateEl.content.cloneNode(true).firstElementChild;
    if (message.id != null) wrapper.dataset.messageId = message.id;
    if (message.client_msg_id) wrapper.dataset.clientMsgId = message.client_msg_id;
    if (message.at) wrapper.dataset.at = message.at;
    wrapper.dataset.userId = message.user.id;

    messageRenderer.fillAvatar(wrapper, message.user);
    this.fillIdentity(wrapper, message.user.name, !!message.user.is_channel_admin, message.user.id, !!message.user.has_subscriber_profile);
    messageRenderer.fillBody(wrapper.querySelector("[data-chat-message-body]"), message.body, !!message.user.is_moderator);

    if (actions && this.moderatorValue) wrapper.appendChild(this.buildModeratorActions(message));

    return wrapper;
  }

  // Shared by buildMessage, buildTipMessage and buildPrivateMessage: the display name is a new-tab
  // link to the sender's dashboard profile for moderators only (live_chat/message_renderer fills it).
  fillIdentity(row, name, isChannelAdmin, userId, hasSubscriberProfile) {
    // Only link a sender who actually has a subscriber profile here - not "isn't a channel
    // admin", which a platform admin with no Subscription would also satisfy and then 404.
    const profileUrl = this.moderatorValue && hasSubscriberProfile && userId != null ? `${this.subscriberProfileBaseUrlValue}/${userId}` : null;
    messageRenderer.fillIdentity(row, name, { isChannelAdmin, profileUrl, badgeTemplate: this.moderatorBadgeTarget });
  }

  // A small toolbar that floats over the top-right of the message and only appears on hover, so
  // it never reserves column width in the chat. Hidden + non-interactive until the row is hovered
  // or focused. Collapsed to just the "..." trigger at rest (see moderatorActionsTemplate); the
  // three labelled actions inside are revealed by expandRailActions/toggleRailActions below. Icons
  // are cloned from the IconComponent-rendered <template>s in the panel markup; the toolbar shell
  // itself is also cloned from a <template> (moderatorActionsTemplate).
  buildModeratorActions(message) {
    const actions = this.moderatorActionsTemplateEl.content.cloneNode(true).firstElementChild;

    const remove = actions.querySelector("[data-chat-message-delete-button]");
    remove.querySelector("[data-chat-rail-icon]").appendChild(this.deleteIconTarget.content.cloneNode(true));
    remove.dataset.chatPanelMessageIdParam = message.id;

    const timeout = actions.querySelector("[data-chat-message-timeout-button]");
    timeout.querySelector("[data-chat-rail-icon]").appendChild(this.timeoutIconTarget.content.cloneNode(true));
    timeout.dataset.chatPanelUserIdParam = message.user.id;

    const ban = actions.querySelector("[data-chat-message-ban-button]");
    ban.querySelector("[data-chat-rail-icon]").appendChild(this.banIconTarget.content.cloneNode(true));
    ban.dataset.chatPanelUserIdParam = message.user.id;

    actions.querySelector("[data-chat-rail-ellipsis]").appendChild(this.ellipsisIconTarget.content.cloneNode(true));

    return actions;
  }

  // A real @media (pointer: coarse) query, not device/UA sniffing - checked live on every read
  // rather than cached at connect(), since the primary pointer can change mid-session (a mouse
  // unplugged from a hybrid laptop, an iPad switching between touch and a trackpad) and a stale
  // cached value would leave the tap-reveal gate below permanently disagreeing with reality.
  get coarsePointer() {
    return window.matchMedia("(pointer: coarse)").matches;
  }

  // mouseenter/mouseleave on the rail container (desktop hover only - on a coarse pointer these
  // never meaningfully fire, but bail explicitly anyway so touch reveal is driven solely by
  // onDocumentClick's tap-to-reveal, never by a stray synthetic hover event).
  expandRailActions(event) {
    if (this.coarsePointer) return;
    this.setRailExpanded(event.currentTarget, true);
  }

  collapseRailActions(event) {
    if (this.coarsePointer) return;
    this.setRailExpanded(event.currentTarget, false);
  }

  // click on the "..." trigger - covers touch tap and keyboard activation (Enter/Space on a
  // focused <button> fires a click event natively, so this needs no separate keyboard handling).
  toggleRailActions(event) {
    const rail = event.currentTarget.closest("[data-chat-rail]");
    if (!rail) return;

    this.setRailExpanded(rail, rail.dataset.expanded !== "true");
  }

  setRailExpanded(rail, expanded) {
    rail.dataset.expanded = String(expanded);
    rail.querySelectorAll("[data-chat-rail-action]").forEach((button) => { button.hidden = !expanded; });

    const ellipsis = rail.querySelector("[data-chat-rail-ellipsis]");
    if (ellipsis) ellipsis.setAttribute("aria-expanded", String(expanded));
  }

  // The coarse-pointer counterpart to expanded above: whether the rail is visible at all (see
  // moderatorActionsTemplate's pointer-coarse:data-[revealed=true]: classes). Hiding always also
  // collapses it, so a rail never reappears already-expanded the next time it's revealed.
  setRailRevealed(rail, revealed) {
    rail.dataset.revealed = String(revealed);
    if (!revealed) this.setRailExpanded(rail, false);
  }

  // Hides every rail except `keep`. Queries [data-chat-rail] fresh on every call rather than
  // caching the currently-open one: rows are removed elsewhere (delete, user_removed, remove-tip),
  // so any cached element reference would dangle.
  hideRailsExcept(keep) {
    this.messagesTarget.querySelectorAll("[data-chat-rail]").forEach((rail) => {
      if (rail === keep) return;
      // focus-within: is not media-gated, so a focused button keeps a hidden rail on screen - and
      // iOS Safari does not reliably blur a focused button when the next tap lands on a
      // non-focusable element - so without this, two rails can stay open simultaneously.
      if (rail.contains(document.activeElement)) document.activeElement.blur();
      this.setRailRevealed(rail, false);
    });
  }

  removeMessage(messageId) {
    if (messageId == null) return;
    const node = this.messagesTarget.querySelector(`[data-message-id="${CSS.escape(String(messageId))}"]`);
    if (node) node.remove();
  }

  // Append a tip as a highlighted inline message, deduped by tip id so a redelivered tip
  // (reconnect) never doubles.
  addInlineTip(tip) {
    if (!tip || tip.id == null) return;
    if (this.messagesTarget.querySelector(`[data-tip-id="${CSS.escape(String(tip.id))}"]`)) return;

    const pinned = this.isPinnedToBottom();
    this.messagesTarget.appendChild(this.buildTipMessage(tip));
    if (pinned) this.scrollToBottom();
  }

  // Clones tipMessageTemplate and fills its slots; mirrors buildMessage plus the amount badge.
  buildTipMessage(tip) {
    const wrapper = this.tipMessageTemplateEl.content.cloneNode(true).firstElementChild;
    wrapper.dataset.tipId = tip.id;
    if (tip.created_at) wrapper.dataset.at = tip.created_at;
    if (tip.user) wrapper.dataset.userId = tip.user.id;

    messageRenderer.fillAvatar(wrapper, tip.user);
    this.fillIdentity(wrapper, (tip.user && tip.user.name) || "", !!(tip.user && tip.user.is_channel_admin), tip.user && tip.user.id, !!(tip.user && tip.user.has_subscriber_profile));
    wrapper.querySelector("[data-chat-tip-amount]").textContent = this.tipAmountLabel(tip);

    const bodyEl = wrapper.querySelector("[data-chat-message-body]");
    const body = (tip.body || "").trim();
    if (body !== "") {
      bodyEl.textContent = body;
    } else {
      bodyEl.remove();
    }

    if (this.moderatorValue) wrapper.appendChild(this.buildTipModeratorActions(tip));

    return wrapper;
  }

  // A hover-revealed remove control for the inline tip, mirroring buildModeratorActions with a
  // single action. Reuses the trash icon <template>; the toolbar shell is cloned from
  // tipModeratorActionsTemplate.
  buildTipModeratorActions(tip) {
    const actions = this.tipModeratorActionsTemplateEl.content.cloneNode(true).firstElementChild;

    const remove = actions.querySelector("[data-chat-message-remove-tip-button]");
    remove.appendChild(this.deleteIconTarget.content.cloneNode(true));
    remove.dataset.chatPanelTipIdParam = tip.id;

    return actions;
  }

  removeInlineTip(id) {
    if (id == null) return;
    const node = this.messagesTarget.querySelector(`[data-tip-id="${CSS.escape(String(id))}"]`);
    if (node) node.remove();
  }

  // A row for this reader alone (the timeout notice, built client-side from the bootstrap). It is
  // never sent to the room, so unlike a room message it carries no dedupe key.
  appendPrivateMessage(message) {
    const pinned = this.isPinnedToBottom();
    const row = this.buildPrivateMessage(message);
    // So history placed later lands in order around it (the client's clock is the only one here).
    row.dataset.at = new Date().toISOString();
    this.messagesTarget.appendChild(row);
    if (pinned) this.scrollToBottom();
    return row;
  }

  // Clones privateMessageTemplate and fills it via the same message renderer and fillIdentity
  // buildMessage uses - `user` is null only for a future system-authored message (not
  // reachable by anything that sends today), so every branch below falls back the same way
  // buildTipMessage already does for a possibly-null tip.user.
  buildPrivateMessage(message) {
    const wrapper = this.privateMessageTemplateEl.content.cloneNode(true).firstElementChild;

    messageRenderer.fillAvatar(wrapper, message.user);
    this.fillIdentity(
      wrapper,
      (message.user && message.user.name) || "",
      !!(message.user && message.user.is_channel_admin),
      message.user && message.user.id,
      !!(message.user && message.user.has_subscriber_profile)
    );
    messageRenderer.fillBody(wrapper.querySelector("[data-chat-message-body]"), message.body, !!(message.user && message.user.is_moderator));

    return wrapper;
  }

  // A moderator removed (shadow-banned) this user from the room (LiveChat::Moderation::BanUser's
  // user_removed event). It is published room-wide, so it reaches the removed user's own browser too - but the
  // whole point of a shadow ban is that their own view never changes, so the self-case returns
  // before touching the DOM at all. Reversing this ordering would have a banned user watch their
  // own message history purge itself: a total self-reveal.
  handleRemoveUser(userId) {
    if (String(userId) === String(this.currentUserIdValue)) return;

    this.messagesTarget
      .querySelectorAll(`[data-user-id="${CSS.escape(String(userId))}"]`)
      .forEach((node) => node.remove());
  }

  // A moderator-applied timeout, learned from the bootstrap after the kick reconnects us (the
  // server disconnects the muted user so their next token is read-only). Unlike handleRemoveUser,
  // which removes the target's rows from everyone else's view, a timeout leaves every existing
  // message exactly where it is; only the composer is disabled, with a countdown that re-enables
  // it once the TTL this echoes reaches zero. seconds is always "time remaining from now," never a
  // stored total, so a token handover mid-timeout simply restarts the countdown from the fresh
  // value; the notice row (reason and moderator) is rendered only on entering the state.
  handleTimedOut(secondsRemaining, reason, by) {
    const seconds = Number(secondsRemaining) || 0;
    if (seconds <= 0) return;

    // The notice row is DOM the permanent panel keeps across a remount, so "entering" is read
    // from it, not from this instance's flag; the row is marked expired once the countdown ends.
    const entering = !this.timedOut && !this.messagesTarget.querySelector('[data-chat-timeout-notice="active"]');
    this.timedOut = true;
    this.setInputDisabled(true);
    // Reuses the same halt used for a disconnect: no automatic resend could land on a message
    // sent right as the mute landed, so it's surfaced as failed with its usual retry affordance.
    this.haltAllPending();
    if (this.hasTimedOutTarget) this.timedOutTarget.classList.remove("hidden");
    if (entering) {
      const row = this.appendPrivateMessage({ user: by || null, body: this.timeoutNoticeBody(reason) });
      row.dataset.chatTimeoutNotice = "active";
    }

    // seconds is the Redis TTL floored to whole seconds, so the mute can outlive it by up to a
    // second; counting one more avoids refreshing into a still-muted token and minting twice.
    this.timeoutDeadlineIso = new Date(Date.now() + (seconds + 1) * 1000).toISOString();
    if (this.timeoutTimer) window.clearInterval(this.timeoutTimer);
    this.tickTimeoutCountdown();
    this.timeoutTimer = window.setInterval(() => this.tickTimeoutCountdown(), 1000);
  }

  // The moderator's reason is free text they typed, rendered through the same textContent path
  // as any chat body. The remaining time lives in the countdown notice, not here.
  timeoutNoticeBody(reason) {
    const body = "You've been timed out.";
    const trimmed = (reason || "").trim();
    return trimmed === "" ? body : `${body} Reason: ${trimmed}`;
  }

  // Reuses utils/live_poll's countdown() (built for the poll deadline countdown) against a local
  // deadline derived once in handleTimedOut, rather than duplicating the remaining/label math -
  // there is no server-provided deadline to parse directly, only a live "seconds left" snapshot.
  tickTimeoutCountdown() {
    const result = countdown(this.timeoutDeadlineIso);
    const remaining = result ? result.remaining : 0;

    if (this.hasTimedOutCountdownTarget) this.timedOutCountdownTarget.textContent = result ? result.label : "0:00";
    if (remaining > 0) return;

    if (this.timeoutTimer) window.clearInterval(this.timeoutTimer);
    this.timeoutTimer = null;
    this.timedOut = false;
    if (this.hasTimedOutTarget) this.timedOutTarget.classList.add("hidden");
    this.messagesTarget.querySelectorAll('[data-chat-timeout-notice="active"]').forEach((row) => {
      row.dataset.chatTimeoutNotice = "expired";
    });
    // The token was minted without SEND_MESSAGE, so only a fresh one can send: cycle the
    // connection and let its bootstrap (send_enabled back on) re-enable the composer.
    if (this.viewer && this.viewer.send_enabled === false && !this.viewer.shadow && this.room) {
      this.room.refresh();
      return;
    }
    this.enableInput();
  }

  // Shows a message under the composer for 4s, clearing any hint already pending so a fast
  // sequence of calls doesn't hide one early. Does not touch enableInput - a hint shown while the
  // composer stays disabled must not re-enable it.
  showHint(message) {
    this.hintTarget.textContent = message;
    this.hintTarget.classList.remove("hidden");

    if (this.hintTimeout) window.clearTimeout(this.hintTimeout);
    this.hintTimeout = window.setTimeout(() => this.hintTarget.classList.add("hidden"), 4000);
  }

  // Every lock on the composer in one place: closed room, timeout, slow mode, a token that cannot
  // send (a shadow-banned viewer sends locally, so theirs stays open), and not being connected.
  enableInput() {
    if (this.closed || this.timedOut || this.slowModeLocked) return;
    if (this.viewer && this.viewer.send_enabled === false && !this.viewer.shadow) return;
    if (this.room && this.room.state !== "connected") return;
    this.setInputDisabled(false);
  }

  setInputDisabled(disabled) {
    this.inputTarget.disabled = disabled;
    this.submitTarget.disabled = disabled;
  }

  isPinnedToBottom() {
    const el = this.messagesTarget;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
  }

  newClientMsgId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    return `m-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}
